(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const state = {
    root: null,
    bar: null,
    cta: null,
    ctaVisible: true,
    observer: null,
    mutationObserver: null,
    unsubscribeVariant: null,
    frame: 0,
  };

  const drawerIsOpen = () => Boolean(
    document.querySelector('cart-drawer[open], cart-drawer.active, cart-drawer.rp-closing')
  );

  const menuIsOpen = () => (
    document.documentElement.classList.contains('menu-drawer-open')
    || Boolean(document.querySelector('#Details-menu-drawer-container[open]'))
  );

  const setBarVisibility = (visible) => {
    const bar = state.bar;
    if (!bar) {
      document.body.classList.remove('rp-mobile-buybar-visible');
      return;
    }

    const shouldShow = Boolean(
      visible
      && mobileQuery.matches
      && !drawerIsOpen()
      && !menuIsOpen()
      && document.documentElement.contains(bar)
    );

    bar.hidden = !shouldShow;
    bar.toggleAttribute('inert', !shouldShow);
    bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    document.body.classList.toggle('rp-mobile-buybar-visible', shouldShow);
  };

  const updateVisibility = () => setBarVisibility(!state.ctaVisible);

  const readSelectedVariant = (root) => {
    const source = root?.querySelector('script[data-selected-variant]');
    if (!source?.textContent?.trim()) return null;
    try {
      return JSON.parse(source.textContent);
    } catch {
      return null;
    }
  };

  const syncBarContent = () => {
    const { root, bar } = state;
    if (!root || !bar) return;

    const variant = readSelectedVariant(root);
    if (variant?.id) {
      bar.dataset.rpVariantId = String(variant.id);
      bar.dataset.rpAvailable = String(Boolean(variant.available));
    }

    const purchase = root.querySelector('[data-rp-variant-fragment="purchase"]');
    const mainButton = purchase?.querySelector('button[name="add"].rp-btn-main');
    const barButton = bar.querySelector('.rp-mobile-buybar__button');
    const price = purchase?.querySelector('.price-item--sale, .price-item--regular, .price-item');
    const barPrice = bar.querySelector('[data-rp-mobile-buybar-price]');

    if (mainButton && barButton && !barButton.matches('[data-rp-wishlist]')) {
      if (mainButton.dataset.rpAvailable) bar.dataset.rpAvailable = mainButton.dataset.rpAvailable;
      if (mainButton.dataset.rpAvailabilityState) {
        bar.dataset.rpAvailabilityState = mainButton.dataset.rpAvailabilityState;
      }
      bar.dataset.rpIsPreorder = String(mainButton.hasAttribute('data-is-preorder'));

      const label = mainButton.querySelector('span:not(.loading__spinner)')?.textContent?.trim()
        || mainButton.textContent.trim();
      if (label && barButton.textContent.trim() !== label) barButton.textContent = label;

      barButton.toggleAttribute('data-is-preorder', mainButton.hasAttribute('data-is-preorder'));
      const disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
      barButton.toggleAttribute('disabled', disabled);
      barButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    if (price && barPrice) {
      const nextPrice = price.textContent.trim().replace(/\s+/g, ' ');
      if (nextPrice && barPrice.textContent.trim().replace(/\s+/g, ' ') !== nextPrice) {
        barPrice.textContent = nextPrice;
      }
    }
  };

  const disconnectIntersectionObserver = () => {
    state.observer?.disconnect();
    state.observer = null;
  };

  const disconnectMutationObserver = () => {
    state.mutationObserver?.disconnect();
    state.mutationObserver = null;
  };

  const bind = () => {
    state.frame = 0;
    const root = document.querySelector('product-info[id^="MainProduct-"]');
    const bar = root?.querySelector('[data-rp-mobile-buybar]');
    const cta = root?.querySelector(
      '[data-rp-variant-fragment="purchase"] product-form button[name="add"].rp-btn-main, '
      + '[data-rp-variant-fragment="purchase"] product-form button.product-form__submit'
    );

    if (state.root !== root) {
      disconnectMutationObserver();
      state.root = root;
      if (root && typeof MutationObserver === 'function') {
        state.mutationObserver = new MutationObserver(scheduleBind);
        state.mutationObserver.observe(root, { childList: true, subtree: true });
      }
    }

    state.bar = bar;
    syncBarContent();

    if (!root || !bar || !cta) {
      disconnectIntersectionObserver();
      state.cta = cta;
      state.ctaVisible = true;
      setBarVisibility(false);
      return;
    }

    if (state.cta === cta && state.observer) {
      updateVisibility();
      return;
    }

    disconnectIntersectionObserver();
    state.cta = cta;
    state.ctaVisible = true;
    setBarVisibility(false);

    if (typeof IntersectionObserver !== 'function') return;
    state.observer = new IntersectionObserver(([entry]) => {
      state.ctaVisible = entry.isIntersecting;
      updateVisibility();
    }, { threshold: 0.01 });
    state.observer.observe(cta);
  };

  function scheduleBind() {
    if (state.frame) return;
    state.frame = window.requestAnimationFrame(bind);
  }

  const scheduleVisibility = () => window.requestAnimationFrame(updateVisibility);

  const teardown = () => {
    if (state.frame) window.cancelAnimationFrame(state.frame);
    state.frame = 0;
    disconnectIntersectionObserver();
    disconnectMutationObserver();
    if (typeof state.unsubscribeVariant === 'function') state.unsubscribeVariant();
    state.unsubscribeVariant = null;
    state.root = null;
    state.bar = null;
    state.cta = null;
    state.ctaVisible = true;
    document.body.classList.remove('rp-mobile-buybar-visible');
  };

  const init = () => {
    if (document.documentElement.dataset.rpMobileBuybarBound === 'true') return;
    document.documentElement.dataset.rpMobileBuybarBound = 'true';
    bind();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      state.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, scheduleBind);
    }

    document.addEventListener('retroparty:cart-drawer-open', scheduleVisibility);
    document.addEventListener('retroparty:cart-drawer-close', scheduleVisibility);
    document.addEventListener('click', scheduleVisibility, true);
    document.addEventListener('shopify:section:load', scheduleBind);
    document.addEventListener('shopify:section:unload', (event) => {
      if (state.root && event.target.contains(state.root)) {
        disconnectIntersectionObserver();
        disconnectMutationObserver();
        state.root = null;
        state.bar = null;
        state.cta = null;
        state.ctaVisible = true;
        document.body.classList.remove('rp-mobile-buybar-visible');
      }
    });
    window.addEventListener('pagehide', teardown, { once: true });

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateVisibility);
    } else {
      mobileQuery.addListener(updateVisibility);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
